import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

import { IconButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { ICommunity } from '../../../types/';

import QuickLaunchComponent from '../../QuickLaunchComponent';
import Tag from '../../Utils/TagComponent';
import { getColorClass } from '../../../common/Utils';
import RequestsComponent from '../JoiningActivity/Requests';
import InvitesComponent from '../JoiningActivity/Invites';

import { RequestProps } from '../JoiningActivity/Requests/Requests.types';
import { InvitesProps } from '../JoiningActivity/Invites/Invites.types';


interface Props {
  community: any;
  tooltips: any;
  profile: any;
  isProtected: boolean;
  isTeamsApp: boolean;
  constants: any;
  govTeamsLogo: any;
  screenWidth: number;
  CommunityActionsComponent: any;
  MembersComponent: any;
  requestsProps: RequestProps;
  pendingInvitesProps: InvitesProps;
  expiredInvitesProps: InvitesProps;
  toggleFavourite(event: React.MouseEvent<any>): void;
}

const DashboardComponent = (props: Props) => {
  const {
    isTeamsApp = false,
    community,
    tooltips,
    profile,
    screenWidth,
    isProtected,
    constants,
    govTeamsLogo,
    CommunityActionsComponent,
    MembersComponent,
    requestsProps,
    pendingInvitesProps,
    expiredInvitesProps,
    toggleFavourite,
  } = props;

  const [hideSponsoredCallout, setHideSponsoredCallout] = React.useState<boolean>(false);
  const sponsoredBadge = useRef<HTMLElement | null>(null);

  const showTeams = community?.isMember && community?.url && community?.iconPreferenceId !== 3;
  const showSharepoint = !!((community?.isMember &&
    community?.sharePointUrl &&
    community?.iconPreferenceId !== 2) ||
    (!profile?.isGuest &&
    !community?.isMember &&
    community?.communityType === 'Open' &&
    community?.sharePointUrl));

  useEffect(() => {
    console.log('GovTEAMS library community dashboard components v1.0.240318');
  }, []);

  /**
   * Retrieves the tags of a community and displays them as a list of tags.
   * @param {ICommunity} community - The community object containing the tags.
   * @returns {JSX.Element} The component that displays the tags.
   */
  const getTags = (community: ICommunity) => {
    const tags = community.tags;

    return (
      <div className='tags'>
        {tags.map((tag, idx) => {
          return <Tag
            searchScreenUrl={constants.SCREEN_SEARCH}
            tag={tag}
            key={idx}
            hideLink={isTeamsApp}
          />;
        })}
      </div>
    );
  };

  return (
    <>
      {community && (
        <>
          <div className='section-twoCol'>
            <div className='section-twoCol-left'>
              <div className={ `page-header ${getColorClass(community.communityType)}`}>
                <h1>{community.displayName}</h1>
                <div className='page-header-status'>
                  <div className='communityTypeBadge mr-1'>
                    {community.communityType}
                  </div>
                  <React.Fragment>
                    {community.sponsored && (
                      <>
                        <div
                          className='sponsored'
                          ref={(sb) => (sponsoredBadge.current = sb)}
                          onMouseOver={() => setHideSponsoredCallout(false)}
                          onMouseOut={() => setHideSponsoredCallout(true)}
                        >
                          S
                        </div>
                        <Callout
                          hidden={hideSponsoredCallout}
                          target={sponsoredBadge.current}
                          calloutMaxWidth={300}
                        >
                          <div className='govTeams-Callout-body'>
                            <h3>Sponsored Communities</h3>
                            <p>{tooltips.SPONSORED_BADGE}</p>
                          </div>
                        </Callout>
                      </>
                    )}
                    {community.isMember && (
                      <IconButton
                        iconProps={{iconName: community.isFavourite ? 'FavoriteStarFill' : 'FavoriteStar'}}
                        text={community.isFavourite ? 'Remove favourite' : 'Favourite community' }
                        ariaLabel={community.isFavourite ? 'Remove favourite' : 'Favourite community'}
                        onClick={toggleFavourite}
                        disabled={!community.isCreated}
                        styles={{ icon: { color: '#fff', fontSize: 20 } }}
                      />
                    )}
                  </React.Fragment>
                </div>
              </div>
              <div className='page-cell' style={{ paddingBottom: 10 }}>
                <div>
                  {community.description && <p>{community.description}</p>}
                  {community.tags && community.tags.length > 0 && (
                    <div>{getTags(community)}</div>
                  )}
                </div>
                <div>
                  <hr />
                  {CommunityActionsComponent}
                </div>
              </div>
            </div>
            <div className='section-twoCol-right'>
              <div className='page-cell'>
                <div>
                  {community.isCreated && <h2>How to use this community</h2>}
                  <div>
                    {community.extendedDescription && (
                      <ReactMarkdown
                        linkTarget={'_blank'}
                      >
                        {community.extendedDescription}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
                <div>
                  <hr />
                  <QuickLaunchComponent
                    community={community}
                    showTeams={showTeams}
                    showSharepoint={showSharepoint}
                    isProtected={isProtected}
                    constants={constants}
                    govTeamsLogo={govTeamsLogo}
                  />
                </div>
              </div>
            </div>
          </div>

          {community && !community.isOwner && (
            <div className='page-cell' style={{ marginTop: 40 }}>
              {MembersComponent}
            </div>
          )}

          {community && community.isOwner && (
            <div style={{ marginTop: 40 }}>
              <div className='page-cell'>
                <h2>Joining activity</h2>
                <div className='pageLayout-threeCol'>
                  <div className='pageLayout-col'>
                    <h3>Requests</h3>
                    <RequestsComponent
                      values={requestsProps.values}
                      tooltips={tooltips}
                      requestChangeRequest={requestsProps.requestChangeRequest}
                    />
                  </div>
                  <div className='pageLayout-col'>
                    <h3>Pending invitations</h3>
                    <InvitesComponent
                      invitations={pendingInvitesProps.invitations}
                      inviteType={'pending'}
                      tooltips={tooltips}
                      requestResendInvite={pendingInvitesProps.requestResendInvite}
                      requestDeleteInvite={pendingInvitesProps.requestDeleteInvite}
                    />
                  </div>
                  <div className='pageLayout-col'>
                    <h3>Expired invitations</h3>
                    <InvitesComponent
                      invitations={expiredInvitesProps.invitations}
                      inviteType={'expired'}
                      tooltips={tooltips}
                      requestResendInvite={expiredInvitesProps.requestResendInvite}
                      requestDeleteInvite={expiredInvitesProps.requestDeleteInvite}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default DashboardComponent;